import React, { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { parentApi, apiError } from "../../lib/api";
import { Card, Coins, Modal } from "../../components/ui";
import IconTile from "../../components/IconTile";
import RewardVisual from "../../components/RewardVisual";
import AuthenticatedImage from "../../components/AuthenticatedImage";
import Mascot from "../../components/Mascot";
import Icon from "../../components/Icon";
import { useAuth } from "../../context/AuthContext";
import { colorFor } from "../../lib/theme";

export default function Inbox() {
  const ctx = useOutletContext();
  const { showEditControls, demoGuard } = useAuth();
  const [chores, setChores] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);

  const load = useCallback(async () => {
    try {
      const [c, r] = await Promise.all([
        parentApi.get("/chores", { params: { status: "pending" } }),
        parentApi.get("/redemptions", { params: { status: "pending" } }),
      ]);
      setChores(c.data.chores); setRedemptions(r.data.redemptions);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); ctx?.refreshBadge?.(); }
  }, [ctx]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, msg) => {
    if (demoGuard()) return;
    try { await fn(); toast.success(msg); await load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (loading) return <div className="grid place-items-center py-20"><div className="w-10 h-10 rounded-full border-4 border-primary/30 border-t-primary animate-spin" /></div>;

  const empty = chores.length === 0 && redemptions.length === 0;

  return (
    <div className="space-y-5" data-testid="inbox">
      <div><h1 className="font-heading font-black text-3xl text-ink">Inbox</h1><p className="text-ink-soft font-medium">Approve finished chores and reward requests.</p></div>

      {empty ? (
        <Card className="p-10 text-center"><Mascot size={110} className="mx-auto mb-2" /><p className="font-heading font-extrabold text-lg text-ink">All caught up!</p><p className="text-ink-soft font-medium">Nothing waiting for your approval.</p></Card>
      ) : (
        <>
          {chores.length > 0 && (
            <div>
              <h2 className="font-heading font-extrabold text-lg text-ink mb-3 flex items-center gap-2"><Icon name="list-checks" className="text-primary" width={20} height={20} /> Chores done ({chores.length})</h2>
              <div className="space-y-3">
                {chores.map((c) => (
                  <Card key={c.id} className="p-4 flex items-center gap-3" data-testid="pending-chore">
                    {c.photo_path ? (
                      <button onClick={() => setLightbox(c.photo_path)} className="w-11 h-11 rounded-2xl overflow-hidden shrink-0 border-2 border-primary/20" data-testid={`chore-photo-${c.id}`} aria-label="View proof photo">
                        <AuthenticatedImage path={c.photo_path} context="parent" alt="Proof" className="w-full h-full object-cover" />
                      </button>
                    ) : (
                      <IconTile name={c.icon || "check"} color={colorFor(c.title)} size={44} />
                    )}
                    <div className="flex-1 min-w-0"><div className="font-bold text-ink truncate">{c.title}</div><div className="flex items-center gap-2"><Coins amount={c.points} className="text-sunny-fg text-sm" />{c.photo_path && <span className="text-xs font-bold text-primary flex items-center gap-0.5"><Icon name="camera" width={12} height={12} /> photo</span>}</div></div>
                    {showEditControls ? (<>
                      <button onClick={() => act(() => parentApi.post(`/chores/${c.id}/decline`), "Sent back to do")} data-testid={`decline-chore-${c.id}`} className="px-3 py-2 rounded-xl bg-slate-100 text-ink-soft font-bold text-sm">Not yet</button>
                      <button onClick={() => act(() => parentApi.post(`/chores/${c.id}/approve`), "Points awarded! 🎉")} data-testid={`approve-chore-${c.id}`} className="px-4 py-2 rounded-xl bg-primary text-white font-bold text-sm shadow-sm">Approve</button>
                    </>) : (
                      <span className="text-xs font-bold text-ink-muted flex items-center gap-1"><Icon name="clock" width={13} height={13} /> Waiting</span>
                    )}
                  </Card>
                ))}
              </div>
            </div>
          )}

          {redemptions.length > 0 && (
            <div>
              <h2 className="font-heading font-extrabold text-lg text-ink mb-3 flex items-center gap-2"><Icon name="gift" className="text-coral" width={20} height={20} /> Reward requests ({redemptions.length})</h2>
              <div className="space-y-3">
                {redemptions.map((r) => (
                  <Card key={r.id} className="p-4 flex items-center gap-3" data-testid="pending-redemption">
                    <RewardVisual reward={{ icon: r.reward_icon, emoji: r.reward_emoji, image_path: r.reward_image_path, title: r.reward_title }} color={colorFor(r.reward_title)} size={44} context="parent" />
                    <div className="flex-1 min-w-0"><div className="font-bold text-ink truncate">{r.child_name} wants <span className="text-coral-dark">{r.reward_title}</span></div><Coins amount={r.cost} className="text-sunny-fg text-sm" /></div>
                    {showEditControls ? (<>
                      <button onClick={() => act(() => parentApi.post(`/redemptions/${r.id}/decline`), "Request declined")} data-testid={`decline-redeem-${r.id}`} className="px-3 py-2 rounded-xl bg-slate-100 text-ink-soft font-bold text-sm">Decline</button>
                      <button onClick={() => act(() => parentApi.post(`/redemptions/${r.id}/approve`), "Reward granted! 🎁")} data-testid={`approve-redeem-${r.id}`} className="px-4 py-2 rounded-xl bg-coral text-white font-bold text-sm shadow-sm">Grant</button>
                    </>) : (
                      <span className="text-xs font-bold text-ink-muted flex items-center gap-1"><Icon name="clock" width={13} height={13} /> Waiting</span>
                    )}
                  </Card>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      <Modal open={!!lightbox} onClose={() => setLightbox(null)} title="Proof photo" testid="photo-lightbox">
        {lightbox && <AuthenticatedImage path={lightbox} context="parent" alt="Chore proof" className="w-full rounded-2xl" />}
      </Modal>
    </div>
  );
}
